import { useState, useEffect } from "react";
import MemberRegistrationForm from "../../../components/Forms/MemberRegistrationForm";
import {
  useCreateMemberMutation,
  useGenerateMemberIdQuery,
} from "../../../store/admin/memberManagement/memberSlice";
import { useGetCompulsoryFeeQuery } from "../../../store/admin/settings/regFeeSlice";
import { toast } from "react-hot-toast";
import uploadImage from "../../../utils/uploadImage";
import { normalizePhoneNumber } from "../../../utils/phoneUtils";

export default function MemberRegHome() {
  const {
    data: memberId = "",
    isFetching: isGeneratingId,
    refetch: refetchMemberId,
    error: memberIdError,
  } = useGenerateMemberIdQuery();
  const { data: regAmount = {}, isLoading: isLoadingFee } =
    useGetCompulsoryFeeQuery();
  const [createMember] = useCreateMemberMutation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [effectiveMemberId, setEffectiveMemberId] = useState("");

  useEffect(() => {
    if (!isGeneratingId) {
      if (memberId) {
        setEffectiveMemberId(memberId);
      } else if (memberIdError) {
        // Generate a random 8-digit number if prefix is missing or API fails
        const randomId = Math.floor(10000000 + Math.random() * 90000000).toString();
        setEffectiveMemberId(randomId);
        
        toast.error(
          (memberIdError?.data?.message || "Failed to generate ID") + 
          ". A random number has been assigned automatically.",
          { duration: 6000 }
        );
      }
    }
  }, [isGeneratingId, memberId, memberIdError]);

  const handleFormSubmit = async (values) => {
    setIsSubmitting(true);
    try {
      const {
        imgFile,
        selectedBreakdown,
        selectedBreakdownWithAmounts,
        selectedAmount,
        setPageName,
        resetForm,
        setImgFile,
        setErrorImg,
        refetchMemberId,
        amount,
        occupation_id, // Exclude occupation_id from backend payload
        ...formikValues
      } = values;
      // Send breakdown with amounts for voluntary fees support
      // Backend now accepts both: array of IDs [1, 3, 8] or array of objects [{id: 1, amount: 10000}, ...]
      let breakdown = selectedBreakdown;
      if (selectedBreakdownWithAmounts && Array.isArray(selectedBreakdownWithAmounts) && selectedBreakdownWithAmounts.length > 0) {
        // Use breakdown with amounts (includes custom amounts for voluntary fees)
        breakdown = selectedBreakdownWithAmounts;
      }
      
      // Normalize phone numbers before sending to backend
      const normalizedPhone = formikValues.phone 
        ? normalizePhoneNumber(formikValues.phone, '234') 
        : null;
      const normalizedNextOfKinPhone = formikValues.next_of_kin_phone 
        ? normalizePhoneNumber(formikValues.next_of_kin_phone, '234') 
        : null;
      
      if (!normalizedPhone) {
        toast.error("Please enter a valid phone number");
        return;
      }
      
      if (!normalizedNextOfKinPhone) {
        toast.error("Please enter a valid next of kin phone number");
        return;
      }
      
      const reqData = {
        ...formikValues,
        phone: normalizedPhone, // Use normalized phone
        next_of_kin_phone: normalizedNextOfKinPhone, // Use normalized next of kin phone
        // branch_id is handled by backend from authenticated staff's active_branch_id
        // Explicitly exclude branch_id for staff registration (not needed - backend uses active_branch_id)
        amount: selectedAmount ?? amount,
        breakdown: breakdown, // Backend accepts array of IDs or array of objects with id and amount
      };
      
      // Remove branch_id from request data for staff registration
      // Backend will use getActiveBranchId() from authenticated staff
      delete reqData.branch_id;
      
      let photoUrl =
        "https://www.gravatar.com/avatar/a208380389f40731d3ac10839ec143a2?d=identicon&r=pg&s=100";
      if (imgFile) {
        photoUrl = await uploadImage(imgFile);
      }
      reqData["photo"] = photoUrl;
      
      const payload = await createMember(reqData);
      const { data, error } = payload;
      if (error) {
        toast.error(error.data?.message || "Failed");
        return;
      }
      if (data.status === false) {
        toast.error(data.message || "An error occurred");
        return;
      }
      toast.success("Member successfully created");
      setPageName("personal_details");
      resetForm();
      refetchMemberId(); // Backend will use active branch from authenticated staff
      setImgFile(null);
      setErrorImg("Click to upload image");
    } catch (error) {
      const message =
        error?.data?.message ||
        error?.error?.message ||
        error?.message ||
        "An unexpected error occurred. Please try again.";
      toast.error(message);
      console.error("Member registration error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="mx-lg-4 min-vh-100 pb-5">
      <MemberRegistrationForm
        memberId={effectiveMemberId}
        isGeneratingId={isGeneratingId}
        regAmount={regAmount}
        isLoadingFee={isLoadingFee}
        onSubmit={handleFormSubmit}
        refetchMemberId={refetchMemberId}
        isSubmitting={isSubmitting}
        generationError={memberIdError}
      />
    </section>
  );
}
